import mitt, {Emitter} from 'mitt';
import {useEffect, useRef} from 'react';

type Events = {
    DATA_GRID_ROW_ADDED: {
        index: number;
        dataGridKey: string;
    },
    DATA_GRID_ROW_REMOVED: {
        index: number;
        dataGridKey: string;
    },
    IMAGE_RETRIEVED_FROM_COMPARABLE: {
        index: number;
        dataGridKey: string;
        imageUrl?: string;
    },
    COMPARABLE_CLICKED: {
        fieldKey: string;
        index: number;
    }
    COMPARABLE_IMAGE_CLICKED: {
        fieldKey: string;
        index: number;
    }
}

const eventBus: Emitter<Events> = mitt<Events>();
//helper to use the keys of the Events type as a type itself (example: DATA_GRID_ROW_ADDED)
type EventNames = keyof Events;

export function useEventListener<T extends EventNames>(
    eventName: T,
    callback: (event: Events[T]) => void
) {
    //use ref so that between renders, so we dont resubscribe every time the component re-renders
    const savedCallback = useRef(callback);

    //instead of unsubscribing and resubscribing every time (every render) we just update the ref with the latest callback
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    //actually subscribe to the event
    useEffect(() => {
        //create a callback here so we can unsubscribe later
        const eventCallback = (data: Events[T]) => savedCallback.current(data);
        //subscribe to the event
        eventBus.on(eventName, eventCallback);
        //clean up function (unsubscribe when component unmounts)
        return () => {
            eventBus.off(eventName, eventCallback);
        };
    }, [eventName]);//really only changes on first render
}

export function emitEvent<T extends EventNames>(eventName: T, data: Events[T]) {
    eventBus.emit(eventName, data);
}
